import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Trophy, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { errorCodeOf } from '../../services/api-error';
import {
  roundScoresService,
  type AttemptType,
  type RoundScore,
  type ScoreCard,
} from '../../services/round-scores';
import { summarise } from '../../utils/round-score-summary';

/** The card as the dialog edits it: types only, since order is the meaning. */
type DraftCards = Record<string, AttemptType[]>;

function toDraft(cards: ScoreCard[]): DraftCards {
  return Object.fromEntries(
    cards.map((card) => [card.round_climb_id, card.attempts.map((a) => a.attempt_type)])
  );
}

interface RoundScoreDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  competitionSlug: string;
  roundId: string;
  score: RoundScore | null;
  /**
   * Narrows the dialog to one problem. The scoreboard opens somebody's whole
   * card at once; the per-block table opens the one cell that was clicked, and
   * has no business showing — or saving — the rest of the round.
   */
  roundClimbId?: string | null;
  /**
   * Whether the round still takes marks. False once it is closed, and then the
   * dialog is a way of reading a card rather than of writing one: no buttons,
   * no save. Defaults to open, since the correction surface is the usual case.
   */
  canScore?: boolean;
  /**
   * Whether the board's regulation tells judged blocks from self-certified ones
   * (OPES): a self-certified block then says its attempts do not count.
   */
  judgedBlocks?: boolean;
  onSaved: (score: RoundScore) => void;
  /** The server having answered that the round is closed — see RoundBlocksTable. */
  onRoundClosed?: () => void;
}

/**
 * One person's card for the whole round, corrected after the fact.
 *
 * Everything is edited locally and committed once. That is not a nicety: a
 * judge fixing a card usually takes two attempts off and puts a different one
 * on, and doing that as live calls would leave a moment where the board says
 * something nobody meant — and would strand it there if the next call failed.
 * So the changes are visible immediately, the save is one request, and closing
 * without saving changes nothing.
 *
 * A top ends a card. Attempts after it never happened, so once a top is on
 * there is nothing more to add until it comes off — which matches the rule the
 * server enforces.
 */
export default function RoundScoreDialog({
  open,
  onOpenChange,
  competitionSlug,
  roundId,
  score,
  roundClimbId,
  canScore = true,
  judgedBlocks = false,
  onSaved,
  onRoundClosed,
}: RoundScoreDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [draft, setDraft] = useState<DraftCards>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !score) return;
    setDraft(toDraft(score.cards));
  }, [open, score]);

  /** The whole round, or the one problem that was clicked. */
  const cards = useMemo(
    () =>
      roundClimbId
        ? (score?.cards ?? []).filter((card) => card.round_climb_id === roundClimbId)
        : (score?.cards ?? []),
    [score, roundClimbId]
  );

  const dirty = useMemo(() => {
    if (!score) return false;
    const original = toDraft(score.cards);
    return cards.some(
      (card) =>
        (draft[card.round_climb_id] ?? []).join(',') !==
        (original[card.round_climb_id] ?? []).join(',')
    );
  }, [cards, draft, score]);

  const setCard = (climbId: string, attempts: AttemptType[]) =>
    setDraft((current) => ({ ...current, [climbId]: attempts }));

  const add = (climbId: string, type: AttemptType) =>
    setCard(climbId, [...(draft[climbId] ?? []), type]);

  const removeAt = (climbId: string, index: number) =>
    setCard(
      climbId,
      (draft[climbId] ?? []).filter((_, i) => i !== index)
    );

  const handleSave = async () => {
    if (!score || saving || !canScore) return;

    setSaving(true);
    try {
      const saved = await roundScoresService.save(
        competitionSlug,
        roundId,
        score.id,
        cards.map((card) => ({
          round_climb_id: card.round_climb_id,
          attempts: draft[card.round_climb_id] ?? [],
        }))
      );
      onSaved(saved);
      onOpenChange(false);
    } catch (error) {
      toast({
        title: t('gymAdmin.roundScores.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
      if (errorCodeOf(error) === 'round_closed') onRoundClosed?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85svh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          {/* Narrowed to one problem, the header has to say which — the card
              below is the only thing on screen and they all look alike. */}
          <DialogTitle>
            {roundClimbId && cards[0]
              ? `${score?.full_name} — ${cards[0].climb_name ?? t('gymAdmin.roundScores.unnamedClimb')}`
              : score?.full_name}
          </DialogTitle>
          <DialogDescription>
            {canScore
              ? t('gymAdmin.roundScores.dialogDescription')
              : t('gymAdmin.roundScores.closedDialogDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {cards.map((card) => {
            const attempts = draft[card.round_climb_id] ?? [];
            const topped = attempts.includes('top');

            return (
              <div
                key={card.round_climb_id}
                className="space-y-2 rounded-lg border p-3"
                data-testid="score-card"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate font-medium">
                      {card.climb_name ?? t('gymAdmin.roundScores.unnamedClimb')}
                    </span>
                    {/* Who may write on it, whatever the regulation. */}
                    {card.requires_judge && (
                      <Badge variant="secondary" className="shrink-0 text-xs font-normal" data-testid="requires-judge-badge">
                        {t('competitionRounds.requiresJudge')}
                      </Badge>
                    )}
                  </span>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="font-mono text-xs">
                      {summarise(attempts)}
                    </Badge>
                    {canScore && attempts.length > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => setCard(card.round_climb_id, [])}
                        data-testid="clear-card"
                      >
                        {t('gymAdmin.roundScores.clearAll')}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Every attempt in the order it happened, each with the x that
                    takes it off. `|` a try, `+` the zone, `T` the top. */}
                <div className="flex flex-wrap items-center gap-1">
                  {attempts.length === 0 && (
                    <span className="text-xs text-muted-foreground">
                      {t('gymAdmin.roundScores.noAttempts')}
                    </span>
                  )}
                  {attempts.map((type, index) => (
                    <span
                      key={`${card.round_climb_id}-${index}`}
                      className={cn(
                        'inline-flex items-center gap-0.5 rounded border px-1.5 py-0.5 font-mono text-sm',
                        type === 'zone' && 'border-yellow-600 text-yellow-600',
                        type === 'top' && 'border-green-600 text-green-600',
                        type === 'attempt' && 'text-muted-foreground'
                      )}
                      data-testid={`attempt-${type}`}
                    >
                      {type === 'zone' ? '+' : type === 'top' ? 'T' : '|'}
                      {canScore && (
                        <button
                          type="button"
                          onClick={() => removeAt(card.round_climb_id, index)}
                          aria-label={t('gymAdmin.roundScores.removeAttempt')}
                          className="rounded-full hover:bg-muted"
                          data-testid="remove-attempt"
                        >
                          <X className="size-3" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>

                {/* Under OPES a self-certified block's attempts are kept and
                    ignored by the ranking: said where they are written. */}
                {judgedBlocks && !card.requires_judge && (
                  <p className="text-xs text-muted-foreground" data-testid="self-certified-note">
                    {t('competitionRankings.selfCertifiedAttempts')}
                  </p>
                )}

                {/* A top ends the card, so there is nothing to add past it —
                    and so does a closed round, which ends every card. */}
                {canScore && !topped && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => add(card.round_climb_id, 'attempt')}
                      data-testid="add-attempt"
                    >
                      {t('judgeBoulder.attempt')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 border-yellow-600 text-yellow-600 hover:bg-yellow-50"
                      onClick={() => add(card.round_climb_id, 'zone')}
                      data-testid="add-zone"
                    >
                      <Plus className="mr-1 size-4" />
                      {t('judgeBoulder.zone')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 border-green-600 text-green-600 hover:bg-green-50"
                      onClick={() => add(card.round_climb_id, 'top')}
                      data-testid="add-top"
                    >
                      <Trophy className="mr-1 size-4" />
                      {t('judgeBoulder.top')}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter>
          {/* Nothing to cancel on a closed round: there is only the way out. */}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {canScore ? t('common.cancel') : t('common.close')}
          </Button>
          {canScore && (
            <Button onClick={handleSave} disabled={!dirty || saving}>
              {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t('common.save')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
